import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { ChevronLeft, ChevronRight, CalendarDays, Newspaper, PartyPopper } from "lucide-react";
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths,
  isSameMonth, isSameDay, parseISO,
} from "date-fns";

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(new Date());
  const navigate = useNavigate();

  useEffect(() => { api.get("/calendar").then((r) => setEvents(r.data)).catch(() => {}); }, []);

  const monthStart = startOfMonth(cursor);
  const gridStart = startOfWeek(monthStart);
  const gridEnd = endOfWeek(endOfMonth(cursor));
  const days = [];
  let day = gridStart;
  while (day <= gridEnd) { days.push(day); day = addDays(day, 1); }

  const eventsOn = (d) => events.filter((e) => { try { return isSameDay(parseISO(e.date), d); } catch { return false; } });
  const selectedEvents = eventsOn(selected);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground"><CalendarDays className="h-5 w-5" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Communications Calendar</h1>
          <p className="text-sm text-muted-foreground">Scheduled publications & upcoming events.</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-xl font-semibold">{format(cursor, "MMMM yyyy")}</h2>
            <div className="flex gap-1">
              <Button variant="outline" size="icon" onClick={() => setCursor(addMonths(cursor, -1))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => { setCursor(new Date()); setSelected(new Date()); }} data-testid="cal-today">Today</Button>
              <Button variant="outline" size="icon" onClick={() => setCursor(addMonths(cursor, 1))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="py-2">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => {
              const evs = eventsOn(d);
              const isSel = isSameDay(d, selected);
              const inMonth = isSameMonth(d, cursor);
              return (
                <button key={d.toISOString()} onClick={() => setSelected(d)} data-testid={`cal-day-${format(d, "yyyy-MM-dd")}`}
                  className={`flex min-h-[64px] flex-col items-start rounded-lg border p-1.5 text-left transition-colors ${isSel ? "border-primary bg-primary/5" : "border-transparent hover:bg-secondary"} ${!inMonth ? "opacity-40" : ""}`}>
                  <span className={`text-xs font-medium ${isSameDay(d, new Date()) ? "flex h-5 w-5 items-center justify-center rounded-full bg-accent text-accent-foreground" : ""}`}>{format(d, "d")}</span>
                  <div className="mt-1 w-full space-y-0.5">
                    {evs.slice(0, 2).map((e, i) => (
                      <div key={`${e.id}-${e.type}-${i}`} className={`truncate rounded px-1 py-0.5 text-[10px] ${e.type === "event" ? "bg-accent/15 text-accent" : "bg-primary/15 text-primary"}`}>{e.title}</div>
                    ))}
                    {evs.length > 2 && <div className="text-[10px] text-muted-foreground">+{evs.length - 2} more</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-heading font-semibold">{format(selected, "EEEE, MMM d")}</h3>
          <div className="mt-4 space-y-3" data-testid="cal-day-events">
            {selectedEvents.length === 0 && <p className="text-sm text-muted-foreground">Nothing scheduled for this day.</p>}
            {selectedEvents.map((e, i) => (
              <button key={`${e.id}-${e.type}-${i}`} onClick={() => navigate(`/submissions/${e.id}`)} data-testid={`cal-event-${e.id}`}
                className="flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-secondary">
                <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${e.type === "event" ? "bg-accent/10 text-accent" : "bg-primary/10 text-primary"}`}>
                  {e.type === "event" ? <PartyPopper className="h-4 w-4" /> : <Newspaper className="h-4 w-4" />}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{e.title}</p>
                  <p className="text-xs text-muted-foreground">{e.type === "event" ? "Event" : "Publication"} · {e.committee || "—"}</p>
                  <div className="mt-1"><StatusBadge status={e.status} /></div>
                </div>
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
